import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { IDailyDeal } from 'models/daily-deals/i-daily-deals';

import { addErrorMessageAction } from 'actions/global-actions';

import { getDailyDealsService } from 'services/daily-deals.service';
import { fetchAllAssets } from 'services/market.service';

import { getAssetIdsFromDeals, transformDealsData } from 'utils/daily-deals';

import { queryKeys } from './query-keys';

interface UseDailyDealsQueryReturn {
  deals: IDailyDeal[];
  dealsAssets: any[];
  loading: boolean;
  error: any;
  validUntil: number | null;
  refresh: () => void;
}

// Fetch daily deals data
const fetchDailyDeals = async (): Promise<{ deals: IDailyDeal[]; validUntil: number | null }> => {
  const response = await getDailyDealsService(new AbortController().signal);
  const dealsData = response.data;

  if (!dealsData.length) {
    return { deals: [], validUntil: null };
  }

  const transformedDeals = transformDealsData(dealsData);
  const validUntil = dealsData[0].valid_until * 1000; // Convert to milliseconds

  return { deals: transformedDeals, validUntil };
};

// Fetch assets for deals
const fetchDealsAssets = async (assetIds: number[]) => {
  if (!assetIds.length) return [];

  const response = await fetchAllAssets(assetIds);
  return response.data || [];
};

export const useDailyDealsQuery = (): UseDailyDealsQueryReturn => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Query for daily deals
  const {
    data: dealsData,
    isLoading: dealsLoading,
    error: dealsError,
  } = useQuery({
    queryKey: queryKeys.dailyDeals.list(),
    queryFn: fetchDailyDeals,
  });

  const deals = dealsData?.deals ?? [];
  const validUntil = dealsData?.validUntil ?? null;

  // Get asset IDs from deals
  const assetIds = getAssetIdsFromDeals(deals);

  // Query for deals assets
  const {
    data: dealsAssets = [],
    isLoading: assetsLoading,
    error: assetsError,
  } = useQuery({
    queryKey: queryKeys.dailyDeals.assets(assetIds),
    queryFn: () => fetchDealsAssets(assetIds),
    enabled: assetIds.length > 0, // Only run if we have asset IDs
  });

  // Handle errors
  useEffect(() => {
    if (dealsError) {
      const errorMessage =
        (dealsError as any)?.response?.data?.message || dealsError?.message || 'Failed to fetch daily deals';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [dealsError, dispatch]);

  useEffect(() => {
    if (assetsError) {
      const errorMessage =
        (assetsError as any)?.response?.data?.message || assetsError?.message || 'Failed to fetch deals assets';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [assetsError, dispatch]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.dailyDeals.list() });
    if (assetIds.length > 0) {
      queryClient.invalidateQueries({ queryKey: queryKeys.dailyDeals.assets(assetIds) });
    }
  };

  return {
    deals,
    dealsAssets,
    loading: dealsLoading || assetsLoading,
    error: dealsError || assetsError,
    validUntil,
    refresh,
  };
};
